import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-chai-ink/10" : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_auto-page-builder-2/artifacts/zhs5mm1o_ChaiedLogo.jpg"
            alt="ChaiED — Simplified"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-16">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-sm uppercase tracking-[0.25em] link-underline pb-1 ${
                  isActive ? "text-chai-red" : "text-chai-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center bg-chai-red text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-chai-ink transition-colors"
        >
          Let&apos;s Talk
        </Link>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(true)}
          data-testid="menu-open-button"
          aria-label="Open menu"
        >
          <Menu size={28} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 bg-chai-ink text-white z-50 flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-heading font-bold text-3xl">Chai<span className="text-chai-yellow">ED</span></span>
              <button onClick={() => setOpen(false)} data-testid="menu-close-button" aria-label="Close menu">
                <X size={32} />
              </button>
            </div>
            <nav className="flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.07 }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-heading text-5xl font-bold tracking-tight hover:text-chai-yellow transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
